import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { ClientOnlyGuard } from './core/auth/guards/client-only.guard';
import { landingRouteGuard } from './core/auth/guards/landing.auth.guard';
import { MfaAuthGuard } from './core/auth/guards/mfaAuth.guard';
import { EmailConfirmationComponent } from './modules/account/email-confirmation/email-confirmation.component';
import { ExpiredActivationComponent } from './modules/account/expired-activation/expired-activation.component';
import { ChangePasswordComponent } from './modules/auth/change-password/change-password.component';
import { RecoverLayoutComponent } from './modules/auth/recover-layout/recover-layout.component';
import { ErrorComponent } from './modules/error/error.component';
import { WizardCompleteConfirmationComponent } from './modules/merchant-wizard/wizard-complete-confirmation/wizard-complete-confirmation.component';
import { userProfileResolver } from './modules/user-profile/user-profile.resolver';
import { UpdateMfaService } from './services/update-mfa.service';
import { UpdateUsernameService } from './services/update-username.service';
import { ResetAuthGuard } from './shared/guards/resetAuth.guard';
import { roleGuard } from './core/auth/guards/role.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboard'
    {
        path: '',
        pathMatch: 'full',
        canActivate: [AuthGuard, landingRouteGuard],
        component: LayoutComponent,
    },

    // User Profile page
    {
        path: '',
        canActivate: [AuthGuard, landingRouteGuard],
        providers: [UpdateUsernameService],
        resolve: {
            userProfileResolver,
        },
        component: LayoutComponent,
        children: [
            {
                path: 'user-profile',
                loadChildren: () => import('app/modules/user-profile/user-profile.routes'),
            },
        ],
    },

    // Authenticated user change password, security settings, etc. pages
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'profile-change-password',
                component: ChangePasswordComponent,
            },
            {
                path: 'security-settings',
                canActivate: [ClientOnlyGuard],
                providers: [UpdateMfaService],
                loadChildren: () => import('app/modules/auth/security-settings/security-settings.routes'),
            },
        ],
    },

    // Empty Guard and Check
    {
        path: '',
        canActivate: [ResetAuthGuard],
        canActivateChild: [ResetAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                component: RecoverLayoutComponent,
                children: [
                    {
                        path: 'recover-account',
                        loadChildren: () => import('app/modules/auth/recover-account/recover-account.routes'),
                    },
                ],
            },
        ],
    },

    // Merchant Account Activation
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'account',
                loadChildren: () => import('app/modules/account/account.routes'),
            },
            {
                path: 'expired-activation',
                component: ExpiredActivationComponent,
            },
            {
                path: 'email-sent',
                component: EmailConfirmationComponent,
            },
        ],
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
            },
            {
                path: 'sign-in',
                loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes'),
            },
            {
                path: 'sign-up',
                loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes'),
            },
        ],
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                component: RecoverLayoutComponent,
                children: [
                    {
                        path: 'email-instructions',
                        loadChildren: () => import('app/modules/auth/email-instructions/email-instructions.routes'),
                    },
                    {
                        path: 'email-verify',
                        loadChildren: () => import('app/modules/auth/email-verify/email-verify.routes'),
                    },
                    {
                        path: 'mobile-verify',
                        loadChildren: () => import('app/modules/auth/mobile-verify/mobile-verify.routes'),
                    },
                    {
                        path: 'recover-username-complete',
                        loadChildren: () => import('app/modules/auth/recover-username-complete/recover-username-complete.routes'),
                    },
                    {
                        path: 'recover-password-complete',
                        loadChildren: () => import('app/modules/auth/recover-password-complete/recover-password-complete.routes'),
                    },
                    {
                        path: 'change-password',
                        loadChildren: () => import('app/modules/auth/recover-change-password/change-password.routes'),
                    },
                ],
            },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
            },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
            },
        ],
    },

    // Error pages
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'error',
                component: ErrorComponent,
                data: {
                    path: '/',
                },
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('app/modules/landing/home/home.routes'),
            },
        ],
    },

    // MFA Routes
    {
        path: '',
        canActivate: [MfaAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'mfa',
                loadChildren: () => import('app/modules/auth/multifactor/multifactor.routes'),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            // change this resolver to get the colleague navigation items
            initialData: initialDataResolver,
        },
        children: [
            // Colleague routes
            {
                path: 'colleague',
                canMatch: [roleGuard({ requiresAdmin: true })],
                loadChildren: () => import('app/modules/colleague/colleague.routes'),
            },
            // Merchant routes
            {
                path: 'merchant',
                loadChildren: () => import('app/modules/merchant/merchant.routes'),
            },
        ],
    },

    // Merchant Wizard
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'merchant-wizard',
                loadChildren: () => import('app/modules/merchant-wizard/merchant.wizard.routes'),
            },
            {
                path: 'merchant-confirmation',
                component: WizardCompleteConfirmationComponent,
            },
            // Error pages
            {
                path: 'error',
                component: ErrorComponent,
                data: {
                    path: '/merchant-wizard',
                },
            },
        ],
    },

    // Post login - Action Required
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'action-required',
                loadChildren: () => import('app/modules/action-required/action-required.routes'),
            },
        ],
    },

    // Wildcard route for invalid URLs
    { path: '**', redirectTo: 'error', pathMatch: 'full' },
];
